<template>
  <section class="flex flex-col lg:flex-row gap-4 lg:gap-8 w-full">
    <div
      class="post-tile-group-content"
      :class="{
        'w-full': !($slots.sidebar && type !== 'sidebar')
      }"
    >
      <atoms-headline
        v-if="title"
        ref="title"
        class="title mb-4 !py-0 font-bold"
        :class="[
          {
            'text-lg': titleSize === 'medium',
            'text-2xl': titleSize === 'large',
            'text-xl md:text-3.5xl': titleSize === 'x-large'
          },
          titleClass
        ]"
      >
        {{ title }}
      </atoms-headline>

      <MoleculesPostTile
        v-if="!_isEmpty(featuredItem)"
        ref="featured"
        layout="horizontal"
        v-bind="featuredItem"
        :type="type"
        :prefix-author-url="prefixAuthorUrl"
        :show-author="showAuthor"
        :class="{
          'mb-8 md:mb-10': type === 'perks'
        }"
        :open-in-new-tab="openInNewTab"
      />

      <hr
        v-if="!_isEmpty(featuredItem) && type === 'expert-analysis'"
        class="border-b border-b-gray-400 my-4 md:my-6"
      />

      <div
        :class="[
          component.main,
          tileClass
        ]"
      >
        <MoleculesPostTile
          v-for="(item, itemIndex) in items"
          :key="itemIndex"
          v-bind="item"
          :type="type"
          :layout="layout"
          :has-border="type === 'perks' || hasBorder"
          :border-class="borderClass"
          :has-divider="
            hasDivider && (
              layout === 'small-tile' ||
              layout !== 'small-tile' && type === 'expert-analysis' && ((itemIndex + 1) % itemsPerRow !== 0 || itemIndex === 0)
            )
          "
          :prefix-author-url="prefixAuthorUrl"
          :show-author="showAuthor"
          :open-in-new-tab="openInNewTab"
        />
      </div>

      <div
        v-if="hasLoadMore"
        :class="{
          'mb-6 md:mb-16': title === 'Top News',
          'mb-10': type !== 'sidebar'
        }"
      >
        <atoms-button
          ref="loadmore"
          theme="link"
          size="large"
          class="text-center mx-auto mt-6 md:mt-10 !p-0 !text-base md:!text-lg"
          @click="handleLoadMore"
        >
          Load More

          <IconsChevron
            type="circle"
            direction="bottom"
            class="fill-dark ml-2.5 group-active:fill-primary group-hover:fill-primary"
          />
        </atoms-button>
      </div>

      <hr
        v-if="hasContentDivider"
        class="border-b border-b-gray-400"
        :class="contentDividerClass"
      />
    </div>

    <div
      v-if="$slots.sidebar && type !== 'sidebar'"
      class="post-tile-group-sidebar min-w-[16rem] lg:max-w-[16rem] flex flex-col gap-8 md:gap-6"
    >
      <slot name="sidebar" />
    </div>
  </section>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'

import {
  EXPERT_EASE_AUTHOR_URL
} from '~/constants/pages/contentful'

defineOptions({
  name: 'MoleculesPostTileGroup'
})

const props = defineProps({
  title: {
    type: String,
    default: '',
    validator: value => !checkStringContainsHtml(value)
  },

  titleSize: {
    type: String,
    default: 'medium',
    validator: value => ['medium', 'large', 'x-large'].includes(value)
  },

  titleClass: {
    type: String,
    default: ''
  },

  featuredItem: {
    type: Object,
    default: () => ({})
  },

  items: {
    type: Array,
    default: () => ([])
  },

  itemsPerRow: {
    type: [Number, String],
    default: 3,
    validator: value => [3, 4].includes(value)
  },

  layout: {
    type: String,
    default: 'vertical',
    validator: value => ['horizontal', 'vertical', 'small-tile'].includes(value)
  },

  type: {
    type: String,
    default: 'expert-analysis',
    validator: value => ['expert-analysis', 'perks', 'sidebar'].includes(value)
  },

  paddingSize: {
    type: String,
    default: 'default',
    validator: value => ['default', 'small'].includes(value)
  },

  hasBorder: {
    type: Boolean,
    default: false
  },

  borderClass: {
    type: String,
    default: ''
  },

  hasDivider: {
    type: [Boolean, Number],
    default: false
  },

  dividerClass: {
    type: String,
    default: ''
  },

  hasLoadMore: {
    type: Boolean,
    default: false
  },

  hasContentDivider: {
    type: Boolean,
    default: false
  },

  contentDividerClass: {
    type: String,
    default: ''
  },

  showAuthor: {
    type: Boolean,
    default: true
  },

  prefixAuthorUrl: {
    type: String,
    default: EXPERT_EASE_AUTHOR_URL
  },

  tileClass: {
    type: String,
    default: ''
  },
  openInNewTab: {
    type: Boolean,
    default: false
  }
})

const component = computed(() => {
  const type = props.type
  const itemsPerRow = props.itemsPerRow
  const paddingSize = props.paddingSize

  const main = (() => {
    let common = getKey(type, {
      'expert-analysis': `grid md:gap-8 md:grid-cols-2 ${getKey(paddingSize, {
        default: 'gap-4',
        small: 'gap-2'
      })}`,
      perks: 'grid gap-4 sm:grid-cols-2 sm:gap-6 md:gap-8',
      sidebar: 'flex flex-col'
    })

    if (['perks', 'expert-analysis'].includes(type)) {
      common += getKey(itemsPerRow, {
        3: ' lg:grid-cols-3',
        4: ' lg:grid-cols-4'
      })
    }

    return common
  })()

  return {
    main
  }
})

const emit = defineEmits(['loadMore'])

function handleLoadMore () {
  emit('loadMore', true)
}
</script>
